import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Code2, FileText, GitBranch } from "lucide-react";
import { API } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { REVIEW_TIERS, REVIEW_PRICE_FROM } from "@/lib/reviewPricing";

/**
 * BuildersPage — `/builders` doorway, Phase 3.
 *
 * Marketing-only page that mirrors `_design-system/pages/builders.html`:
 *   Hero + "what you get" card → what gets reviewed today → "Your reviewer
 *   arrives briefed" 3-column flow → Narrative anchor → list-price tiers
 *   (lib/reviewPricing) → reviewers on Xpert (live data only) → Final CTA.
 *
 * Concierge mode (2026-10-09): every review is done by a named human, today
 * the founder. Invented experts, testimonials, "or it's free" promises,
 * "Most popular" and the old, stale tier prices were deleted.
 *
 * Conversion target: visitor → "Get my first review" → /get-verified.
 */
export default function BuildersPage() {
  const navigate = useNavigate();
  const goSubmit  = () => navigate("/get-verified");
  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />
      <Hero onSubmit={goSubmit} />
      <WhatWeReview />
      <BriefedFlow />
      <Narrative />
      <Pricing onSubmit={goSubmit} />
      <FeaturedShippers navigate={navigate} />
      <FinalCTA onSubmit={goSubmit} navigate={navigate} />
      <MarketingFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero — get the green check
// ---------------------------------------------------------------------------

const TOOL_WORDMARKS = ["Cursor", "Claude Code", "Vercel", "Replit", "ChatGPT"];

function Hero({ onSubmit }) {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden="true"
           className="absolute -top-32 -right-24 w-[640px] h-[640px] rounded-full bg-brand-green/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
              The human review layer for AI-built work
            </p>
            <h1 className="font-display text-5xl sm:text-6xl text-ink leading-[1.05] mt-5">
              Get{" "}
              <em className="not-italic">
                <span className="italic text-brand-green">the green check.</span>
              </em>
            </h1>
            <p className="mt-6 text-lg text-slateline-600 leading-relaxed max-w-xl">
              Ship your AI-built code, specs and decisions with a named human
              review.{" "}
              <strong className="text-ink font-semibold">Written verdict.</strong>{" "}
              <strong className="text-ink font-semibold">24 hours, 48 hours or a week.</strong>{" "}
              From ${REVIEW_PRICE_FROM}.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={onSubmit}
                      className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm shadow-sm transition-colors">
                <span className="font-mono text-base">$</span>
                Submit my AI output for review
                <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#how"
                 className="inline-flex items-center h-12 px-2 text-sm font-medium text-ink hover:text-brand-green transition-colors">
                See how it works ↓
              </a>
            </div>

            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-slateline-600">
              {[
                "Reviewed by a named human",
                "Your Claude / ChatGPT / Cursor conversation comes along as the brief",
                "AI tools plus human judgement, disclosed",
              ].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-brand-green" />
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-7 pt-6 border-t border-slateline-200 flex flex-wrap items-center gap-x-6 gap-y-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-slateline-500">
                Works with output from
              </span>
              {TOOL_WORDMARKS.map((w) => (
                <span key={w} className="font-display text-base font-semibold text-slateline-500/90 tracking-tight">
                  {w}
                </span>
              ))}
            </div>
          </div>

          {/* What a review is made of. A description, not a sample verdict. */}
          <aside className="bg-white border border-slateline-200 rounded-xl p-6 shadow-sm flex flex-col gap-3.5">
            <div className="flex items-center gap-2 pb-3 border-b border-slateline-100">
              <span className="w-2 h-2 rounded-full bg-brand-green" />
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slateline-500">
                What you send, what you get
              </span>
            </div>
            <PreviewRow k="you send" v="Code, a spec or a strategy doc" />
            <PreviewRow k="plus"     v="The AI conversation that produced it" />
            <PreviewRow k="reviewer" v="Ankit Kumar, founder of Xpert" green />
            <PreviewRow k="speed"    v="24 hours · 48 hours · 1 week" />
            <PreviewRow k="you get"  v="Written verdict, findings, what to change, confidence" />
            <div className="mt-1 p-3 bg-brand-greenPale border border-brand-green/30 rounded-md flex items-center gap-2.5 text-[13px] leading-snug">
              <Check className="w-4 h-4 text-brand-green flex-shrink-0" />
              <div>
                <strong className="text-ink font-semibold">Xpert Verified badge</strong>{" "}
                <span className="text-slateline-600">· private unless you choose to publish it</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function PreviewRow({ k, v, green = false }) {
  return (
    <div className="grid grid-cols-[88px_1fr] gap-2.5 items-start text-[13px] leading-snug">
      <span className="font-mono text-[12px] text-slateline-500 pt-0.5">{k}</span>
      <span className={green ? "text-brand-green font-semibold" : "text-ink"}>{v}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// What we review — 6 artifact cards
// ---------------------------------------------------------------------------

// What the founder reviews today (founder decision 2026-10-07). Add a card
// only when a named reviewer covers that kind of work.
const ARTIFACTS = [
  { icon: Code2,     name: "Code",           blurb: "SaaS deploys, payment integrations, auth flows, webhook handlers, AI-generated migrations." },
  { icon: FileText,  name: "Product specs",  blurb: "PRDs, onboarding flows, feature scopes. Whether the thing AI helped you spec is the right thing to build." },
  { icon: GitBranch, name: "Strategy calls", blurb: "Pricing, positioning, go-to-market plans. The calls where AI gave you five framings and no answer." },
];

function WhatWeReview() {
  return (
    <section id="usecases" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SectionHead
        eyebrow="What gets reviewed today"
        title={<>Code, specs and strategy.<br />Reviewed by the founder.</>}
        subtitle="Today every review is done by Ankit Kumar, who founded Xpert. As demand grows we add hand-picked specialists for other kinds of work."
      />

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ARTIFACTS.map(({ icon: Icon, name, blurb }) => (
          <div key={name}
               className="bg-white border border-slateline-200 hover:border-brand-green/30 hover:shadow-sm rounded-xl p-6 flex flex-col gap-3 transition-all">
            <div className="w-10 h-10 rounded-lg bg-brand-greenPale text-brand-green grid place-items-center">
              <Icon className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <h3 className="font-display text-lg text-ink leading-tight">{name}</h3>
            <p className="text-sm leading-relaxed text-slateline-600">{blurb}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// How the AI brief works — 3-column flow
// ---------------------------------------------------------------------------

function BriefedFlow() {
  return (
    <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20">
      <SectionHead
        eyebrow="How the AI brief works"
        title={<>Your reviewer arrives <em className="not-italic"><span className="italic text-brand-green">briefed.</span></em></>}
        subtitle="No 15 minutes of &quot;let me catch you up.&quot; Your AI conversation comes attached and the reviewer reads it first. You skip context. You go straight to judgment."
      />

      <div className="mt-10 grid lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-6 lg:gap-4 items-stretch">
        <BriefCol
          tag="Step 1 · You"
          title="Paste your AI conversation"
          body="Drop in the full thread from Claude, ChatGPT, Cursor, Gemini, whatever you used. Plus the artifact: the code, spec or memo."
          card={
            <>
              <div className="text-[11px] text-slateline-400 mb-1">user · 4:12pm</div>
              "Add Stripe subscription webhooks to my Next.js app and handle retries…"
              <div className="text-[11px] text-slateline-400 mt-2 mb-1">claude · 4:13pm</div>
              "Here's a handler with signature checks. <em>Test idempotency before production.</em>"
            </>
          }
        />
        <FlowArrow />
        <BriefCol
          green
          tag="Step 2 · Xpert"
          title="It travels with your request"
          body="Your AI conversation comes attached, and the reviewer reads it first. The artifact and any files you add come along too."
          card={
            <>
              <div className="text-[11px] text-slateline-400 mb-2">illustration · what the reviewer opens</div>
              <div className="space-y-1.5">
                <div><strong className="text-ink">Your goal,</strong> as you wrote it.</div>
                <div><strong className="text-ink">The conversation,</strong> as you pasted it.</div>
                <div><strong className="text-ink">The artifact,</strong> plus any files you attached.</div>
              </div>
            </>
          }
        />
        <FlowArrow />
        <BriefCol
          tag="Step 3 · Reviewer"
          title="Reviewer arrives knowing"
          body="They read the conversation and the artifact first. The review starts at the problem, not at the introduction."
          card={
            <>
              <div className="text-[11px] text-slateline-400 mb-1">illustration · your review</div>
              Reads the conversation first, then writes up findings,
              what to change and how confident each call is.
            </>
          }
        />
      </div>
    </section>
  );
}

function BriefCol({ tag, title, body, card, green = false }) {
  return (
    <div className={[
      "rounded-xl p-6 flex flex-col gap-3 border",
      green ? "bg-brand-greenPale border-brand-green/30" : "bg-white border-slateline-200",
    ].join(" ")}>
      <span className={[
        "text-[11px] font-bold uppercase tracking-[0.14em]",
        green ? "text-brand-green" : "text-slateline-500",
      ].join(" ")}>{tag}</span>
      <h3 className="font-display text-xl text-ink leading-tight">{title}</h3>
      <p className="text-sm leading-relaxed text-slateline-600">{body}</p>
      <div className="mt-1 bg-white border border-slateline-200 rounded-lg p-3.5 text-[13px] leading-relaxed text-slateline-700">
        {card}
      </div>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="hidden lg:flex items-center justify-center text-2xl text-slateline-400" aria-hidden="true">
      →
    </div>
  );
}

// ---------------------------------------------------------------------------
// Narrative anchor — the story behind Xpert
// ---------------------------------------------------------------------------

function Narrative() {
  return (
    <section className="bg-canvas border-t border-b border-slateline-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-20 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
              The story behind Xpert
            </p>
            <h2 className="font-display text-3xl sm:text-4xl text-ink leading-tight mt-4">
              Every AI tool forgot to add{" "}
              <em className="not-italic"><span className="italic text-brand-green">a review button.</span></em>
            </h2>
          </div>
          <div className="text-[17px] leading-[1.65] text-slateline-700 space-y-4">
            <p>
              AI gets you eighty percent of the way to a usable draft. The last twenty percent is the part with consequences — and it's the part where AI either hedges or politely declines to be useful. "Not legal advice." "Consult a CPA." "Review with your team."
            </p>
            <p>
              So you sit on the draft. Or you ship it scared. Or you build a habit of half-finishing things because the gates between "AI says it's done" and "I'm confident enough to publish" have no human inside them.
            </p>
            <p>
              Xpert is the human inside the gate. A named reviewer on every artifact you ship. One list price per turnaround. Your AI conversation as the brief, so the review starts where you got stuck. Submit, sleep, ship.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pricing — 3 tiers
// ---------------------------------------------------------------------------

// Every tier gets the same review; only the turnaround differs. Prices come
// from lib/reviewPricing, which mirrors backend TURNAROUND_RATES.
const INCLUDED = [
  "Your AI conversation attached as the brief",
  "Written verdict with key findings",
  "What to change, as action items",
  "A confidence level on the verdict",
  "Xpert Verified badge, private unless you publish it",
];

function Pricing({ onSubmit }) {
  return (
    <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SectionHead
        eyebrow="Pricing"
        title={<>Three turnarounds.<br />One list price each.</>}
        subtitle="No retainer, no intake call, no proposal. Pick a speed and submit. Same review, same reviewer, whichever you pick."
      />

      <div className="mt-10 grid sm:grid-cols-3 gap-4">
        {REVIEW_TIERS.map((t) => (
          <div key={t.key} className="rounded-xl p-7 flex flex-col gap-4 border bg-white border-slateline-200">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-5xl font-semibold text-ink leading-none">${t.price}</span>
              <span className="text-sm text-slateline-500">/ review</span>
            </div>
            <div className="text-sm font-semibold text-brand-green">{t.label} turnaround</div>
            <button onClick={onSubmit}
                    className="mt-auto w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-md font-semibold text-sm transition-colors border border-slateline-300 hover:border-ink text-ink">
              Submit at ${t.price} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white border border-slateline-200 rounded-xl p-6 sm:p-7">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slateline-500">Every review includes</div>
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slateline-700">
          {INCLUDED.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-brand-green flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-sm text-slateline-500 text-center mt-8">
        Pay per review, no subscription.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Featured Shippers — live fetch with fallback
// ---------------------------------------------------------------------------

// Live directory data only. There is no fallback bench: when the API is empty
// or fails, the section is not rendered (Supply F3).
function FeaturedShippers({ navigate }) {
  const [experts, setExperts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/experts?available=true&limit=4`)
      .then((r) => {
        if (cancelled) return;
        const live = Array.isArray(r.data) ? r.data : [];
        if (live.length) {
          setExperts(live.slice(0, 4).map((e) => ({
            initials: (e.name || "??").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase(),
            name: e.name || "Expert",
            role: e.title || "Senior practitioner",
            chip: (e.expertise && e.expertise[0]) || "Reviewer",
            body: e.bio || "",
            // Async prices are fixed per turnaround, the same for every
            // reviewer (Supply F12), so the card shows the list price floor,
            // and only for someone who takes written reviews (the persona
            // rows do not, so they show no price and no review offer).
            takesReviews: e.accepts_async_review === true,
            async: `from $${REVIEW_PRICE_FROM}`,
            id: e.id,
          })));
        }
      })
      .catch(() => { /* no fallback: section stays hidden */ });
    return () => { cancelled = true; };
  }, []);

  if (!experts.length) return null;

  return (
    <section className="bg-white border-t border-b border-slateline-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionHead
          eyebrow="On Xpert today"
          title="The people listed on Xpert."
          subtitle="Live from the directory. Today, written reviews are done by Ankit Kumar, the founder."
        />

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {experts.map((e) => (
            <article key={e.name + (e.id || "")}
                     onClick={() => e.id && navigate(`/experts/${e.id}`)}
                     className={[
                       "bg-white border border-slateline-200 rounded-xl p-6 flex flex-col gap-3.5",
                       e.id ? "cursor-pointer hover:border-slateline-300 transition-colors" : "",
                     ].join(" ")}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-brand-greenPale text-brand-green font-display text-lg font-semibold grid place-items-center flex-shrink-0">
                  {e.initials}
                </div>
                <div>
                  <div className="font-display text-base text-ink leading-tight">{e.name}</div>
                  <div className="text-[12px] text-slateline-500 mt-0.5">{e.role}</div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full bg-brand-greenPale text-brand-green text-[11px] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
                {e.chip}
              </span>
              {e.body && <p className="text-[13px] leading-relaxed text-slateline-600 line-clamp-4">{e.body}</p>}
              {e.takesReviews && (
                <div className="pt-3 border-t border-slateline-100 text-xs">
                  <div className="text-slateline-400 uppercase tracking-wider font-semibold text-[10px]">Written review</div>
                  <div className="text-ink font-display font-semibold text-base mt-0.5">{e.async}</div>
                </div>
              )}
            </article>
          ))}
        </div>

        <p className="text-sm text-center mt-10">
          <a href="/experts" className="text-brand-green hover:underline font-semibold">
            Browse the directory →
          </a>
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Final dark CTA
// ---------------------------------------------------------------------------

function FinalCTA({ onSubmit, navigate }) {
  return (
    <section className="relative bg-ink text-white overflow-hidden">
      <div aria-hidden="true"
           className="absolute -bottom-32 -left-24 w-[640px] h-[640px] rounded-full bg-brand-green/25 blur-3xl pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">
          The human review layer for AI-built work
        </p>
        <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight mt-3">
          AI built it.<br />
          <em className="not-italic">
            <span className="italic text-brand-green">Get the green check.</span>
          </em>
        </h2>
        <p className="text-white/70 mt-5 max-w-xl mx-auto">
          Send the work tonight. Get a written verdict from a named reviewer.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button onClick={onSubmit}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-green hover:bg-brand-greenHover text-white font-semibold text-sm shadow-sm transition-colors">
            Get my first review <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={() => navigate("/become-expert")}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-white/30 hover:border-white text-white font-medium text-sm transition-colors">
            Or be an expert
          </button>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-white/70">
          <span><strong className="text-white">Named reviewer.</strong> 24 hours, 48 hours or a week.</span>
          <span><strong className="text-white">Submit, sleep, ship.</strong></span>
          <span><strong className="text-white">From ${REVIEW_PRICE_FROM}.</strong></span>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function SectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-green">{eyebrow}</p>
      <h2 className="font-display text-4xl sm:text-[44px] text-ink leading-[1.1] mt-4">{title}</h2>
      {subtitle && (
        <p className="text-lg text-slateline-600 mt-4 leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}
